<template>
  <div
    v-if="
      !generalStatus.has_pending_cargo_policy_request &&
      !generalStatus.cargo_policy_on_file_and_active
    "
    class="flex flex-col items-center justify-center gap-6 rounded-md border border-steel-300 py-8"
    :class="isTabletOrMobile ? 'h-[600px]' : ''"
  >
    <img
      src="@/assets/cargo-icon.svg"
      alt="Cargo"
    />
    <div
      :class="isTabletOrMobile ? 'text-center' : 'flex flex-col items-center'"
    >
      <h1 class="mb-2 text-xl font-medium text-neutral-boldest">
        Share your Cargo Policy
      </h1>
      <p class="px-2 text-sm font-normal text-neutral-subtle-bold lg:px-0">
        Submit a request for Highway to retrieve your Motor Truck Cargo
        <br v-if="!isTabletOrMobile" />
        insurance policy so we can verify your cargo coverage exclusions.
      </p>
    </div>
    <div
      :class="[
        'flex w-full gap-3',
        isTabletOrMobile
          ? 'flex-col-reverse px-2'
          : 'flex-row items-center justify-center',
      ]"
    >
      <button
        :class="[
          'rounded-md text-sm font-medium',
          isTabletOrMobile
            ? 'h-12 w-full bg-steel-20 text-neutral-subtle-bold'
            : 'h-10 bg-steel-20 px-3 text-neutral-subtle-bold hover:bg-hovered-gray active:bg-pressed-gray',
        ]"
        @click="onLearnMoreCargo"
      >
        Learn more
      </button>

      <button
        :class="[
          'rounded-md text-sm font-medium',
          isTabletOrMobile
            ? 'h-12 w-full bg-neutral-subtle-yellow text-neutral-boldest'
            : 'h-10 bg-neutral-subtle-yellow px-3 text-neutral-boldest hover:bg-neutral-subtle-yellow-bold active:bg-neutral-subtle-yellow-boldest',
        ]"
        @click="onVerifyMyExclusions"
      >
        Verify my exclusions
      </button>
    </div>
  </div>
  <div
    v-else-if="
      generalStatus.has_pending_cargo_policy_request &&
      !generalStatus.cargo_policy_on_file_and_active
    "
    class="flex flex-1 flex-col items-center justify-center gap-6"
  >
    <img
      src="@/assets/pending-cargo-exclusions.svg"
      alt="Pending Cargo Policy Request"
    />
    <div
      :class="isTabletOrMobile ? 'text-center' : 'flex flex-col items-center'"
    >
      <h1 class="mb-2 text-xl font-medium text-neutral-boldest">
        Pending Cargo Policy
      </h1>
      <p class="text-sm font-normal text-neutral-subtle-bold">
        Please instruct your insurance agency to email your
        <br v-if="!isTabletOrMobile" />
        Motor Truck Cargo insurance policy to
        <a
          href="mailto:cargopolicy@insurance.highway.com"
          class="text-sun-700 underline hover:opacity-90"
          >cargopolicy@insurance.highway.com</a
        >
      </p>
    </div>
  </div>
  <div v-else>
    <section>
      <div
        :class="[
          isTabletOrMobile
            ? 'flex flex-col gap-3'
            : 'items-left flex justify-between',
        ]"
      >
        <h4 class="hidden text-xl text-steel-1100 lg:block">Exclusions</h4>
        <filter-search-bar
          class="mb-5 lg:mb-0"
          placeholder="Search Exclusions"
          v-model="exclusionsSearch"
          className="search-exclusions"
        ></filter-search-bar>
      </div>

      <div>
        <vue-collapsible-panel-group
          v-for="exclusion in Object.keys(filteredExclusions)"
          accordion
          :key="exclusion"
          class="exclusions-vcp"
        >
          <vue-collapsible-panel :expanded="!isTabletOrMobile">
            <template #title>
              <div class="flex gap-2 py-2">
                <img
                  v-if="exclusion === 'Commodities'"
                  class="-mt-4"
                  src="@/assets/insurance/commodities-icon.svg"
                  alt="icon"
                />
                <img
                  v-if="exclusion === 'Perils'"
                  class="-mt-4"
                  src="@/assets/insurance/perils-icon.svg"
                  alt="icon"
                />
                <img
                  v-if="exclusion === 'Territories'"
                  class="-mt-4"
                  src="@/assets/insurance/territories-icon.svg"
                  alt="icon"
                />
                <div>
                  <h4 class="font-semibold text-dark-gray">
                    {{ exclusion }}
                  </h4>
                  <p class="text-sm font-normal text-body-color">
                    {{ commoditiesTotal(exclusion) || 0 }} items are
                    {{ exclusionFilterText }}.
                  </p>
                </div>
              </div>
            </template>
            <template #content>
              <div class="border-gray border-t pb-2 pt-4">
                <div v-if="filteredExclusions[exclusion]?.length === 0">
                  <p class="text-sm font-normal text-body-color">
                    No items found.
                  </p>
                </div>
                <div
                  :class="[
                    isTabletOrMobile
                      ? 'flex flex-col gap-4'
                      : 'grid w-full grid-cols-3 gap-4',
                  ]"
                >
                  <div v-if="loading">
                    <ExclusionItemSkeleton
                      class="border-gray delay-50 mb-4 flex flex-col rounded-md border transition duration-100"
                      v-for="i in 5"
                      :key="i"
                    />
                  </div>
                  <div v-else>
                    <div
                      v-for="(item, index) in filteredExclusions[exclusion]"
                      :key="item.is_type"
                    >
                      <div
                        v-if="index % 3 === 0"
                        class="border-gray delay-50 mb-4 flex-1 flex-col rounded-md border transition duration-100"
                        :class="{
                          'policy-pill': item.page,
                        }"
                      >
                        <exclusion-details
                          :item="item"
                          :attachmentUrl="cargoPolicy?.attachment_url"
                          @click="handleItemSelection(item)"
                          :isSelected="selectedItems.includes(item.is_type)"
                        ></exclusion-details>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div v-if="loading">
                      <ExclusionItemSkeleton
                        class="border-gray delay-50 mb-4 flex flex-col rounded-md border transition duration-100"
                        v-for="i in 5"
                        :key="i"
                      />
                    </div>
                    <div
                      v-else
                      v-for="(item, index) in filteredExclusions[exclusion]"
                      :key="item.is_type"
                    >
                      <div
                        v-if="index % 3 === 1"
                        class="border-gray delay-50 mb-4 flex flex-col rounded-md border transition duration-100"
                        :class="{
                          'policy-pill': item.page,
                        }"
                      >
                        <exclusion-details
                          :item="item"
                          :attachmentUrl="cargoPolicy?.attachment_url"
                          @click="handleItemSelection(item)"
                          :isSelected="selectedItems.includes(item.is_type)"
                        ></exclusion-details>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div v-if="loading">
                      <ExclusionItemSkeleton
                        class="border-gray delay-50 mb-4 flex flex-col rounded-md border transition duration-100"
                        v-for="i in 5"
                        :key="i"
                      />
                    </div>
                    <div
                      v-else
                      v-for="(item, index) in filteredExclusions[exclusion]"
                      :key="item.is_type"
                    >
                      <div
                        v-if="index % 3 === 2"
                        class="border-gray delay-50 mb-4 flex flex-col rounded-md border transition duration-100"
                        :class="{
                          'policy-pill': item.page,
                        }"
                      >
                        <exclusion-details
                          :item="item"
                          :attachmentUrl="cargoPolicy?.attachment_url"
                          @click="handleItemSelection(item)"
                          :isSelected="selectedItems.includes(item.is_type)"
                        ></exclusion-details>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </vue-collapsible-panel>
        </vue-collapsible-panel-group>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, defineProps } from 'vue';
import { useStore } from 'vuex';
import { useScreenSize } from '@/composables/useScreenSize';
import { useToast } from 'vue-toast-notification';
import FilterSearchBar from '@/components/FilterSearchBar.vue';
import {
  VueCollapsiblePanelGroup,
  VueCollapsiblePanel,
} from '@dafcoe/vue-collapsible-panel';
import ExclusionDetails from '@/components/ExclusionDetails.vue';
import ExclusionItemSkeleton from '@/components/ExclusionItemSkeleton.vue';

const store = useStore();
const toast = useToast();
const { screenSize } = useScreenSize();
const exclusionsSearch = ref('');
const filters = ref({
  coveredItems: false,
  excludedItems: true,
});
const selectedItems = ref([]);
const isTabletOrMobile = computed(
  () => screenSize.value === 'tablet' || screenSize.value === 'mobile'
);

defineProps({
  loading: { type: Boolean, default: false },
});

const generalStatus = computed(
  () => store.state.users.userLoadBoardAccess?.user
);

const policyExclusions = computed(() => {
  return {
    Commodities: cargoPolicy.value?.commodities,
    Territories: cargoPolicy.value?.territories,
  };
});

const cargoPolicy = computed(
  () =>
    store.state.insurance?.carrierInsurancePolicies.find(
      (policy) => policy.cargo_policy
    )?.cargo_policy
);

const exclusionFilterText = computed(() => {
  return filters.value.excludedItems ? 'excluded' : 'covered';
});

function handleItemSelection(item) {
  const itemsIndex = selectedItems.value.indexOf(item.is_type);

  if (itemsIndex > -1) {
    selectedItems.value.splice(itemsIndex, 1);
  } else {
    selectedItems.value.push(item.is_type);
  }
}

async function onVerifyMyExclusions() {
  try {
    await store.dispatch('insurance/createCargoPolicyRequest');
    await store.dispatch('users/checkLoadBoardAccess');
  } catch (error) {
    console.error('Error verifying exclusions:', error);
    showErrorToast(error);
  }
}

const filteredExclusions = computed(() => {
  if (exclusionsSearch.value) {
    const neitherFilters =
      !filters.value.excludedItems && !filters.value.coveredItems;
    const excludedFilter = !neitherFilters && filters.value.excludedItems;

    return Object.entries(policyExclusions.value).reduce(
      (acc, [key, items]) => {
        acc[key] = items.filter((el) => {
          // Always exclude items with is_excluded: true unless explicitly showing excluded items
          if (el.is_excluded === true && !filters.value.excludedItems) {
            return false;
          }

          if (!neitherFilters) {
            return (
              el.is_excluded === excludedFilter &&
              (el.is_type
                .replace(/_/g, ' ')
                .toLowerCase()
                ?.includes(exclusionsSearch.value.toLowerCase()) ||
                el?.referenced_text
                  ?.toLowerCase()
                  ?.includes(exclusionsSearch.value.toLowerCase()))
            );
          }

          return (
            el.is_type
              .replace(/_/g, ' ')
              .toLowerCase()
              ?.includes(exclusionsSearch.value.toLowerCase()) ||
            el?.referenced_text
              ?.toLowerCase()
              ?.includes(exclusionsSearch.value.toLowerCase())
          );
        });

        return acc;
      },
      {}
    );
  } else if (filters.value.excludedItems) {
    return Object.entries(policyExclusions.value).reduce(
      (acc, [key, items]) => {
        acc[key] = items?.filter((el) => el.is_excluded);

        return acc;
      },
      {}
    );
  } else if (filters.value.coveredItems) {
    return Object.entries(policyExclusions.value).reduce(
      (acc, [key, items]) => {
        acc[key] = items.filter((el) => !el.is_excluded);

        return acc;
      },
      {}
    );
  }

  // Filter out items with is_excluded: true when no specific filter is applied
  return Object.entries(policyExclusions.value).reduce((acc, [key, items]) => {
    acc[key] = items.filter((el) => !el.is_excluded);
    return acc;
  }, {});
});

function showErrorToast(error) {
  toast?.open({
    message:
      error?.response?.data?.[0] ||
      'Something went wrong with your request. Please try again',
    position: 'top-right',
    duration: 5000,
    type: 'error',
  });
}

function commoditiesTotal(type) {
  const isExcluded = filters.value.excludedItems;
  return filteredExclusions.value[type]?.filter((el) =>
    isExcluded ? el.is_excluded : !el.is_excluded
  )?.length;
}

function onLearnMoreCargo() {
  window.open(
    `https://service.highway.com/en/articles/11794225-why-does-highway-need-to-verify-my-exclusions`,
    '_blank'
  );
}
</script>

<style>
.exclusions-vcp .vcp.vcp--expandable .vcp__header {
  @apply !bg-white;
}

.search-exclusions {
  height: 2.5rem !important;
  min-width: 300px;
}
.policy-pill:hover {
  box-shadow: 0px 5px 10px rgba(109, 113, 129, 0.14);
  @apply border-gray-over-dark;
}
.policy-pill:hover h4 {
  @apply font-medium;
}
</style>
